/**
 * Loading placeholders and the app's single spinner.
 *
 * WHY THESE EXIST
 * ---------------
 * A skeleton is the honest alternative to rendering a defaulted zero. Every
 * screen that cannot yet show a number shows one of these instead, so the
 * layout is reserved at its real size and nothing on screen claims to be a
 * measurement. The shimmer itself is the `.cv-skeleton` class in index.css —
 * one definition, one animation, so 14 files stop inventing their own.
 *
 * ANNOUNCEMENT POLICY
 * -------------------
 * Skeletons are decoration: the bars are `aria-hidden` so a screen reader never
 * tries to read a grid of grey rectangles. `SkeletonText` — the default that
 * `QueryState` falls back to — carries the one polite `role="status"`, because
 * something has to say "loading". `SkeletonTile` and `SkeletonRows` deliberately
 * do not: they render in multiples (four tiles, twenty rows) and four
 * simultaneous live regions is noise, not accessibility. The container owns the
 * announcement in those cases.
 */

import React, { useEffect, useState } from 'react';

/** Numbers mean pixels; strings pass through as authored CSS lengths. */
const len = (v) => (typeof v === 'number' ? `${v}px` : v || undefined);

/** Clamp a count prop to something renderable — `rows={0}` should still draw. */
const count = (v, fallback) => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/**
 * Subscribed, not sampled: the OS setting can change while the app is open and
 * a spinner that keeps spinning after the user asks for less motion is exactly
 * the complaint the preference exists to fix.
 */
function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(REDUCED_MOTION).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(REDUCED_MOTION);
    const onChange = (event) => setReduced(event.matches);
    setReduced(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari 13 and the iOS WebViews still in the field only have addListener.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return reduced;
}

/**
 * One shimmer bar.
 *
 * @param {object} props
 * @param {number|string} [props.w]  width — number = px, string = any CSS length
 * @param {number|string} [props.h]  height, same rule
 * @param {string} [props.rounded]   radius utility; overrides `.cv-skeleton`'s
 *   default 6px because utilities are emitted after components
 */
export function Skeleton({ w = '100%', h = 12, className = '', rounded = 'rounded' }) {
  return (
    <span
      aria-hidden="true"
      className={`cv-skeleton block ${rounded} ${className}`.trim()}
      style={{ width: len(w), height: len(h) }}
    />
  );
}

/**
 * Paragraph placeholder. Widths are ragged and the last line is short, because
 * a stack of identical full-width bars reads as a table, not as prose.
 */
export function SkeletonText({ lines = 3, className = '' }) {
  const n = count(lines, 3);
  const widths = ['100%', '94%', '88%', '97%', '91%'];

  return (
    <div role="status" className={`space-y-2.5 ${className}`.trim()}>
      <span className="sr-only">Loading</span>
      {Array.from({ length: n }, (_, i) => (
        <Skeleton key={i} h={12} w={i === n - 1 && n > 1 ? '60%' : widths[i % widths.length]} />
      ))}
    </div>
  );
}

/**
 * Table-body placeholder. Returns bare `<tr>` elements so it drops straight into
 * a `<tbody>` — wrapping them in a div would make the browser hoist them out of
 * the table and the column widths would collapse.
 *
 * Widths are derived from the cell index rather than `Math.random()`: a random
 * width changes on every re-render, which makes the whole table twitch while a
 * background refetch is in flight.
 */
export function SkeletonRows({ rows = 5, cols = 4 }) {
  const r = count(rows, 5);
  const c = count(cols, 4);
  const widths = ['72%', '48%', '86%', '56%', '64%', '40%'];

  return (
    <>
      {Array.from({ length: r }, (_, row) => (
        <tr key={row} aria-hidden="true" className="h-row-lg">
          {Array.from({ length: c }, (_, col) => (
            <td key={col} className="border-b border-line px-3 align-middle">
              {/* The first column is the identity column in every table here
                  (agent, caller, date) and is always the widest. */}
              <Skeleton h={12} w={col === 0 ? '78%' : widths[(row * 3 + col) % widths.length]} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * StatTile's footprint, to the pixel: label line, 28px value block (the height
 * of `text-metric`), delta chip beside it, sparkline strip, denominator line.
 * It carries the card recipe itself so a loading KPI row looks like a KPI row
 * and the grid does not resize when the real numbers land.
 */
export function SkeletonTile() {
  return (
    <div aria-hidden="true" className="rounded-lg border border-line bg-surface p-card">
      <Skeleton h={12} w={112} />
      <div className="mt-3 flex items-end gap-2">
        <Skeleton h={28} w={88} rounded="rounded" />
        <Skeleton h={20} w={64} rounded="rounded-sm" />
      </div>
      <Skeleton className="mt-3" h={28} w="100%" />
      <Skeleton className="mt-3" h={12} w={132} />
    </div>
  );
}

/**
 * The only spinner in the app. Decorative by definition — the control that owns
 * it sets `aria-busy`, so announcing it twice is worse than not at all.
 *
 * Under `prefers-reduced-motion` this becomes a static ring rather than a
 * stalled arc: the global reduce rule clamps animations to one 0.01ms
 * iteration, which would otherwise freeze the arc mid-sweep and read as a
 * broken graphic instead of a deliberate one.
 */
export function Spinner({ size = 16, className = '' }) {
  const reduced = useReducedMotion();
  const px = len(count(size, 16));

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      role="presentation"
      viewBox="0 0 24 24"
      width={px}
      height={px}
      className={`shrink-0 ${reduced ? '' : 'animate-spin'} ${className}`.replace(/\s+/g, ' ').trim()}
    >
      {/* currentColor: the spinner is the same ink as the label it sits next to,
          so it needs no colour token of its own and stays legible on a brand
          button, a ghost button, and a dimmed chart alike. */}
      <circle
        cx="12"
        cy="12"
        r="9.5"
        fill="none"
        stroke="currentColor"
        strokeOpacity={reduced ? 0.4 : 0.2}
        strokeWidth="2.5"
      />
      {!reduced && (
        <circle
          cx="12"
          cy="12"
          r="9.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          // ~22% of the 59.7px circumference: long enough to read as motion,
          // short enough that the ring behind it stays visible.
          strokeDasharray="13 47"
        />
      )}
    </svg>
  );
}
